import { useLocation, useParams } from 'react-router';
import { JSX, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { useIPE } from 'src/redux/slices/Interview/hooks';
import { useTransferGuard } from 'src/components/NewRenterInterface/Home/QuestionnairePage/hooks/useTransferGuard';
import { useCertificationHubBackRoute } from 'src/components/NewRenterInterface/CertificationHub/useCertificationHubBackRoute';
import LoadingScreen from './screens/LoadingScreen';
import InterviewShell from './InterviewShell';

export default function InterviewRoute(): JSX.Element | null {
  const { surveyId, memberId } = useParams<{
    surveyId: string;
    memberId?: string;
  }>();
  const dispatch = useDispatch();
  const { callIPE } = useIPE();
  const location = useLocation();

  // ENG-4049: the cert-status "Complete about N questions" row navigates here
  // with route state asking for questions. Forward that intent so the first
  // work item resolved from the IPE response is the question group rather than
  // a pending signature. Anything else (direct URL, refresh, "Continue
  // interview") leaves the canonical signature-first priority alone.
  const requestedWorkItem =
    (location.state as { requestedWorkItem?: string } | null)
      ?.requestedWorkItem === 'questions'
      ? 'questions'
      : null;

  const sId = surveyId ? parseInt(surveyId, 10) : undefined;

  const dvtRedirectPath = memberId
    ? `/renter/interview/${surveyId}/data-transfer/members/${memberId}`
    : `/renter/interview/${surveyId}/data-transfer`;
  const { isChecking, shouldRender } = useTransferGuard(sId, dvtRedirectPath);

  useEffect(() => {
    if (!surveyId || !shouldRender) {
      return;
    }
    const parsedId = parseInt(surveyId, 10);
    dispatch(
      interviewActions.initInterview({
        surveyId: parsedId,
        onBehalfOf: memberId ? Number(memberId) : null,
        requestedWorkItem,
      })
    );
    callIPE({
      surveyIdOverride: parsedId,
      onBehalfOfOverride: memberId ? Number(memberId) : null,
    });
    return () => {
      dispatch(interviewActions.resetInterview());
    };
  }, [surveyId, shouldRender]); // eslint-disable-line react-hooks/exhaustive-deps

  const { backRoute: hubBackRoute } = useCertificationHubBackRoute({
    surveyId: sId ?? null,
    userId: memberId ? Number(memberId) : null,
  });

  useEffect(() => {
    if (hubBackRoute && shouldRender) {
      dispatch(interviewActions.setBackPath(hubBackRoute));
    }
  }, [hubBackRoute, shouldRender, dispatch]);

  if (!surveyId) {
    return null;
  }
  if (isChecking) {
    return <LoadingScreen />;
  }
  if (!shouldRender) {
    return null;
  }

  return <InterviewShell />;
}
